import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as THREE from 'three';
import { Color } from 'three';

import {
  NFT_TEXTURE_BELOW_BLACKOUT,
  NFT_COLOR_ABOVE_BLACKOUT,
} from '../constants';

import { FloorType } from '../types/floor';

import {
  getCoordinatesFromId,
  getWorldPositionFromNFT,
  isCoordinateAnNFT,
} from '../utils/floor_utils';

import { useNFTContext } from '../context/NFTContext';
import { useTargetContext } from '../context/TargetContext';
import { useSearchContext } from '../context/SearchContext';

const Colors = ({
  width,
  height,
  boxSize,
  floorMap,
}: {
  width: number;
  height: number;
  boxSize: number;
  floorMap: FloorType;
}) => {
  const { target } = useTargetContext();
  const { filteredNFTs, setSelectedNFT, selectedNFT, isNFTPlacingMode } =
    useNFTContext();
  const { isFiltered } = useSearchContext();

  const refInstancedMesh = useRef<THREE.InstancedMesh>(null);
  const cubeGeometry = useMemo(
    () => new THREE.BoxGeometry(boxSize * 0.985, boxSize, boxSize * 0.985),
    [boxSize]
  );

  const [shouldRefreshMaterial, setShouldRefreshMaterial] = useState(false);

  const [cubeMaterial, setCubeMaterial] =
    useState<THREE.MeshStandardMaterial>();

  const refreshMaterial = useCallback(() => {
    setCubeMaterial(new THREE.MeshStandardMaterial({}));
    setShouldRefreshMaterial(false);
  }, []);

  useEffect(() => refreshMaterial(), [refreshMaterial]);
  useEffect(() => setShouldRefreshMaterial(true), []);

  const cubeMesh = useMemo(
    () => new THREE.Mesh(cubeGeometry.clone(), cubeMaterial?.clone()),
    [cubeGeometry, cubeMaterial]
  );

  const changeSelectedNFT = (id: number | undefined) => {
    if (id !== undefined && !isNFTPlacingMode) {
      const coordinates = getCoordinatesFromId(id, width, height);
      const nftObject = isCoordinateAnNFT(
        coordinates.x,
        coordinates.y,
        floorMap
      );

      if (nftObject) {
        if (nftObject.roomId !== selectedNFT?.roomId) {
          setSelectedNFT(nftObject);
          target === 'dev' && console.log('NFT selected: ' + nftObject.roomId);
        }
      }
    }
  };

  useEffect(() => {
    if (refInstancedMesh.current && cubeMaterial) {
      target === 'dev' && console.log('Draw Colors()');

      if (shouldRefreshMaterial) refreshMaterial();

      let counter = 0;

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const id = counter++;

          const coordinates = getCoordinatesFromId(id, width, height);
          const nft = isCoordinateAnNFT(coordinates.x, coordinates.y, floorMap);

          const actualMapIconSize =
            nft &&
            `${nft?.endX - nft?.startX + 1}x${nft?.endY - nft?.startY + 1}`;
          // TODO: for now disable rendering of map icons for small rooms due to load
          // should be enabled after atlas is coming from the backend in future
          if (
            nft &&
            (!nft.mapIconUrl ||
              actualMapIconSize === '2x1' ||
              actualMapIconSize === '1x1')
          ) {
            // draw only one big box for the whole nft
            if (
              nft.isPlaced &&
              nft.endX > -1 &&
              nft.startX > -1 &&
              nft.endY > -1 &&
              nft.startY > -1 &&
              nft.startX === x &&
              nft.startY === y
            ) {
              cubeMesh.scale.set(1, 1, 1);

              const amountOfBoxesX = nft.endX - nft.startX + 1;
              const amountOfBoxesZ = nft.endY - nft.startY + 1;

              const scale = new THREE.Vector3(
                amountOfBoxesZ * 0.999,
                1,
                amountOfBoxesX * 0.999
              );

              let position = getWorldPositionFromNFT(
                nft,
                boxSize,
                amountOfBoxesX,
                amountOfBoxesZ
              ) ?? {
                x: Number.MAX_SAFE_INTEGER,
                y: Number.MAX_SAFE_INTEGER,
                z: Number.MAX_SAFE_INTEGER,
              };

              position.y = NFT_TEXTURE_BELOW_BLACKOUT;

              if (isFiltered || filteredNFTs.length) {
                if (
                  filteredNFTs.findIndex((n) => n.roomId === nft.roomId) >= 0
                ) {
                  position.y = NFT_COLOR_ABOVE_BLACKOUT;
                }
              }

              cubeMesh.position.set(position.x, position.y, position.z);
              cubeMesh.scale.multiply(scale);
            }

            refInstancedMesh.current.setColorAt(id, new Color(nft.color));
          } else {
            // remove this box from the view, it's not needed
            cubeMesh.position.set(
              Number.MAX_SAFE_INTEGER,
              Number.MAX_SAFE_INTEGER,
              Number.MAX_SAFE_INTEGER
            );
          }

          cubeMesh.updateMatrix();
          refInstancedMesh.current.setMatrixAt(id, cubeMesh.matrix);
          refInstancedMesh.current.instanceMatrix.needsUpdate = true;
        }
      }

      if (refInstancedMesh.current.instanceColor)
        refInstancedMesh.current.instanceColor.needsUpdate = true;

      refInstancedMesh.current.instanceMatrix.needsUpdate = true;
    }
  }, [
    boxSize,
    cubeMaterial,
    cubeMesh,
    filteredNFTs,
    floorMap,
    height,
    width,
    refreshMaterial,
    target,
    shouldRefreshMaterial,
    isFiltered,
  ]);

  if (!cubeMaterial || !cubeGeometry) {
    return null;
  }

  return (
    <>
      <instancedMesh
        ref={refInstancedMesh}
        args={[undefined, undefined, width * height]}
        position={[0, 0, 0]}
        renderOrder={130}
        geometry={cubeMesh.geometry}
        material={cubeMesh.material}
        onClick={(e) => {
          e.stopPropagation();
          changeSelectedNFT(e.instanceId);
        }}
      ></instancedMesh>
    </>
  );
};

export default Colors;
